/**
 * PostHog product analytics. No-ops when analytics is off or the project key
 * is missing (local/QA/UAT/prod builds without VITE_POSTHOG_KEY).
 */
import { config } from '@/config'
import { logger } from '@utils/logger'
import { posthog } from '@services/posthogClient'

const DEFAULT_POSTHOG_HOST = 'https://aqua-obs.tredence.com/posthog'

let initialized = false

function analyticsReady(): boolean {
  return Boolean(config.features.analytics && config.services.posthogKey)
}

/** Super property so every event (pageview, autocapture, capture) is filterable per cluster. */
function registerClusterName(client: { register: (props: Record<string, string>) => void }): void {
  const name = config.services.k8sClusterName
  if (!name) {
    return
  }
  client.register({ k8s_cluster_name: name })
}

export function initPosthog(): void {
  if (initialized || !analyticsReady()) {
    return
  }
  const key = config.services.posthogKey
  if (!key) {
    return
  }
  const host = config.services.posthogHost || DEFAULT_POSTHOG_HOST
  posthog.init(key, {
    api_host: host,
    ui_host: host,
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    persistence: 'localStorage+cookie',
    // `loaded` runs before the initial pageview, so the super property is on every event.
    loaded: (client) => {
      registerClusterName(client)
    },
  })
  initialized = true
  logger.info('PostHog analytics initialized')
}

export function identifyPosthogUser(user: {
  id: string
  email?: string
  display_name?: string
  tenant_id?: string | null
  tenant_name?: string | null
}): void {
  if (!initialized && analyticsReady()) {
    initPosthog()
  }
  if (!initialized) {
    return
  }
  posthog.identify(user.id, {
    email: user.email,
    name: user.display_name,
    tenant_id: user.tenant_id ?? undefined,
    tenant_name: user.tenant_name ?? undefined,
  })
}

export function capturePosthogEvent(
  event: string,
  properties?: Record<string, string | number | boolean | null>
): void {
  if (!initialized) {
    return
  }
  posthog.capture(event, properties)
}

export function resetPosthog(): void {
  if (!initialized) {
    return
  }
  posthog.reset()
}

export function isPosthogInitialized(): boolean {
  return initialized
}
